import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { T_LeadsNature } from 'types/app.type';

import { NotificationService } from '@/services/Notification/service';

interface UseLeadsProps {
  apiEndpoint: string;
  isSignedIn?: boolean;
}

interface DismissedLeadEntry {
  id: string;
  dismissedAt: number;
  expiryDays: number | null;
}

const COOKIE_PREFIX = 'dismissedLeads_';

/** Cookie helpers */
const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${value}; path=/; domain=.sky.co.nz; secure; SameSite=None`;
};

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

/** Load dismissed leads from cookies */
const loadDismissedLeads = (): DismissedLeadEntry[] => {
  try {
    const keys = document.cookie
      .split('; ')
      .map(c => c.split('=')[0])
      .filter(k => k.startsWith(COOKIE_PREFIX));

    return keys
      .map(k => {
        const value = getCookie(k);
        if (!value) return null;
        return JSON.parse(decodeURIComponent(value)) as DismissedLeadEntry;
      })
      .filter(Boolean) as DismissedLeadEntry[];
  } catch {
    return [];
  }
};

export const useLeadsNurture = ({ apiEndpoint, isSignedIn }: UseLeadsProps) => {
  const [allLeads, setAllLeads] = useState<T_LeadsNature[]>([]);
  const [bottomLeads, setBottomLeads] = useState<T_LeadsNature[]>([]);
  const [popupLeads, setPopupLeads] = useState<T_LeadsNature[]>([]);
  const [activePopup, setActivePopup] = useState<T_LeadsNature | null>(null);
  const [dismissedLeads, setDismissedLeads] = useState<DismissedLeadEntry[]>(() =>
    loadDismissedLeads()
  );

  const timersRef = useRef<Record<string, NodeJS.Timeout>>({});
  const dismissedRef = useRef(dismissedLeads);
  const popupLeadsRef = useRef<T_LeadsNature[]>([]);
  dismissedRef.current = dismissedLeads;
  popupLeadsRef.current = popupLeads;

  // Track which leads have already shown in this session
  const shownLeadsSession = useRef<Set<string>>(new Set());

  const notificationService = new NotificationService(axios.create({ baseURL: apiEndpoint }));
  const location = window.location;

  /** Save dismissed leads to cookies */
  const saveDismissed = (list: DismissedLeadEntry[]) => {
    setDismissedLeads(list);
    list.forEach(entry => {
      const cookieName = COOKIE_PREFIX + entry.id;
      setCookie(cookieName, encodeURIComponent(JSON.stringify(entry)));
    });
  };

  const isLeadDismissed = (id: string) => dismissedRef.current.some(d => d.id === id);
  const hasShownInSession = (id: string) => shownLeadsSession.current.has(id);
  const markShownInSession = (id: string) => shownLeadsSession.current.add(id);

  const isTargetPage = (pages: string[]) => {
    const currentUrl = window.location.href.split('?')[0];
    return pages.some(page => currentUrl === page);
  };

  const resetIdleTimer = (lead: T_LeadsNature) => {
    if (isLeadDismissed(lead.id) || activePopup || hasShownInSession(lead.id)) return;

    clearTimeout(timersRef.current[lead.id]);

    timersRef.current[lead.id] = setTimeout(() => {
      if (
        !isLeadDismissed(lead.id) &&
        !hasShownInSession(lead.id) &&
        isTargetPage(lead.pageTarget)
      ) {
        setActivePopup(lead);
        markShownInSession(lead.id);
      }
    }, lead.timeInSecond * 1000);
  };

  const stopTimer = (id: string) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
  };

  const filterLeads = (allLeads: T_LeadsNature[]) =>
    allLeads
      .filter(notif => notif.scope !== (isSignedIn ? 'loggedOut' : 'loggedIn'))
      .filter(notif => (notif.pageTarget.length ? isTargetPage(notif.pageTarget) : true));

  const dismissLead = (lead: T_LeadsNature) => {
    const newEntry: DismissedLeadEntry = {
      id: lead.id,
      dismissedAt: Date.now(),
      expiryDays: typeof lead.sessionExpireDays === 'number' ? lead.sessionExpireDays : null,
    };

    const updated = [...dismissedRef.current.filter(d => d.id !== lead.id), newEntry];

    saveDismissed(updated);
    setActivePopup(null);
    stopTimer(lead.id);
  };

  const engageLead = (lead: T_LeadsNature, url: string) => {
    dismissLead(lead);
    window.location.href = url;
  };

  /** Fetch leads once on mount */
  useEffect(() => {
    notificationService.getLeads().then((result: T_LeadsNature | never[]) => {
      const leadsArray: T_LeadsNature[] = Array.isArray(result) ? result : [result];
      setAllLeads(leadsArray);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Filter leads whenever location or sign-in state changes */
  useEffect(() => {
    if (allLeads.length === 0) return;
    const filtered = filterLeads(allLeads);
    setBottomLeads(filtered.filter(l => l.alignment === 'Bottom'));
    setPopupLeads(filtered.filter(l => l.alignment === 'Pop-Up'));
  }, [allLeads, location.pathname, isSignedIn]);

  /** Setup timers and user activity tracking */
  useEffect(() => {
    popupLeads.forEach(lead => resetIdleTimer(lead));

    const handleActivity = () => {
      if (activePopup) return;
      popupLeadsRef.current.forEach(lead => resetIdleTimer(lead));
    };

    const events = ['mousemove', 'keydown', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, handleActivity));

    return () => {
      Object.keys(timersRef.current).forEach(stopTimer);
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
    // eslint-disable-next-line
  }, [location.pathname, isSignedIn, activePopup]);

  return { bottomLeads, popupLeads, activePopup, dismissLead, engageLead };
};
